// Extracted from `pages/CustomerDetailPage.js` (P2 refactor step 4f — cierre).
// Tab "Datos del cliente" — 1:1 move del JSX. Muestra las secciones
// Datos del cliente / Datos del representante (EMPRESA) / Datos de contacto
// (EMPRESA) / Facturación / Ejecutivo de Ventas + Notas y el footer con
// el botón Editar/Guardar. El estado (`editing`, `editData`, `users`,
// validaciones) y los handlers viven en el padre y se inyectan como
// props para no cambiar la funcionalidad ni el estilo.
import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Edit2, Save, X } from "lucide-react";
import {
  ValidationHint,
  inputBorderForValidation,
  statusLabels,
  statusColors,
} from "./utils";
import {
  COUNTRY_DIRECTORY,
  formatCountryLabel,
  detectCountryFromPhone,
  formatPhoneWithCountry,
} from "../../utils/countries";

export const DatosTab = ({
  customer,
  editing,
  setEditing,
  editData,
  setEditData,
  users,
  services,
  docValidation,
  repDocValidation,
  phoneValidation,
  emailValidation,
  ibanValidation,
  handleSave,
  canEdit,
  formatDateTime,
}) => {
  return (
    <Card className="bg-[#0A0A35] border-[#1E1E5F]">
      <CardContent className="pt-6 space-y-8">

        {/* === SECTION: Datos del cliente === */}
        <section>
          <h4 className="text-[#42E695] uppercase text-xs font-semibold tracking-wider mb-4 pb-2 border-b border-[#1E1E5F]">
            Datos del cliente
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Código de cliente */}
            <div>
              <Label className="text-slate-400 text-sm">Código de cliente:</Label>
              <p className="text-[#42E695] text-xl font-bold mt-1">{customer.customer_id}</p>
            </div>

            {/* Type-specific identity fields */}
            {customer.customer_type === "RESIDENCIAL" ? (
              <>
                <div>
                  <Label className="text-slate-400 text-sm">Nombre</Label>
                  {editing ? (
                    <Input
                      value={editData.nombre || ""}
                      onChange={(e) => setEditData({ ...editData, nombre: e.target.value })}
                      className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    />
                  ) : (
                    <p className="text-white mt-1">{customer.nombre || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Apellidos</Label>
                  {editing ? (
                    <Input
                      value={editData.apellidos || ""}
                      onChange={(e) => setEditData({ ...editData, apellidos: e.target.value })}
                      className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    />
                  ) : (
                    <p className="text-white mt-1">{customer.apellidos || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Estado</Label>
                  {editing ? (
                    <Select
                      value={editData.status}
                      onValueChange={(v) => setEditData({ ...editData, status: v })}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                        <SelectItem value="ACTIVO" className="text-white">Activo</SelectItem>
                        <SelectItem value="IMPAGO" className="text-white">Impago</SelectItem>
                        <SelectItem value="LEAD" className="text-white" disabled>Lead (automático)</SelectItem>
                        <SelectItem value="BAJA" className="text-white" disabled>Baja (automático)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`mt-1 ${statusColors[customer.status]}`}>
                      {statusLabels[customer.status]}
                    </Badge>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-2">
                  <Label className="text-slate-400 text-sm">Razón Social</Label>
                  {editing ? (
                    <Input
                      value={editData.razon_social || ""}
                      onChange={(e) => setEditData({ ...editData, razon_social: e.target.value })}
                      className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    />
                  ) : (
                    <p className="text-white mt-1">{customer.razon_social || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Estado</Label>
                  {editing ? (
                    <Select
                      value={editData.status}
                      onValueChange={(v) => setEditData({ ...editData, status: v })}
                    >
                      <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                        <SelectItem value="ACTIVO" className="text-white">Activo</SelectItem>
                        <SelectItem value="IMPAGO" className="text-white">Impago</SelectItem>
                        <SelectItem value="LEAD" className="text-white" disabled>Lead (automático)</SelectItem>
                        <SelectItem value="BAJA" className="text-white" disabled>Baja (automático)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`mt-1 ${statusColors[customer.status]}`}>
                      {statusLabels[customer.status]}
                    </Badge>
                  )}
                </div>
              </>
            )}

            {/* Identity document */}
            <div>
              <Label className="text-slate-400 text-sm">
                {customer.customer_type === "EMPRESA"
                  ? "CIF/NIF"
                  : (customer.document_type === "NIE"
                      ? "NIE"
                      : customer.document_type === "PASAPORTE"
                        ? "Pasaporte"
                        : customer.document_type === "DNI"
                          ? "DNI"
                          : "Documento")}
              </Label>
              {editing ? (
                <div className="mt-1 flex gap-2">
                  {customer.customer_type !== "EMPRESA" && (
                    <Select
                      value={editData.document_type || "DNI"}
                      onValueChange={(v) => setEditData({ ...editData, document_type: v })}
                    >
                      <SelectTrigger data-testid="detail-document-type-select" className="w-32 bg-[#0A0A35] border-[#1E1E5F] text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                        <SelectItem value="DNI" className="text-white">DNI</SelectItem>
                        <SelectItem value="NIE" className="text-white">NIE</SelectItem>
                        <SelectItem value="PASAPORTE" className="text-white">Pasaporte</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Input
                    value={customer.customer_type === "EMPRESA" ? editData.cif || "" : editData.dni_nie || ""}
                    onChange={(e) => setEditData({
                      ...editData,
                      [customer.customer_type === "EMPRESA" ? "cif" : "dni_nie"]: e.target.value
                    })}
                    className={`flex-1 bg-[#0A0A35] text-white ${inputBorderForValidation(docValidation)}`}
                  />
                </div>
              ) : (
                <p className="text-white mt-1">
                  {customer.customer_type === "EMPRESA" ? customer.cif || "-" : customer.dni_nie || "-"}
                </p>
              )}
              {editing && <ValidationHint result={docValidation} />}
            </div>

            {/* Dirección Fiscal */}
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-sm">Dirección Fiscal</Label>
              {editing ? (
                <Input
                  value={editData.direccion_fiscal || ""}
                  onChange={(e) => setEditData({ ...editData, direccion_fiscal: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              ) : (
                <p className="text-white mt-1">{customer.direccion_fiscal || "-"}</p>
              )}
            </div>

            {/* Código Postal */}
            <div>
              <Label className="text-slate-400 text-sm">Código Postal</Label>
              {editing ? (
                <Input
                  value={editData.codigo_postal || ""}
                  onChange={(e) => setEditData({ ...editData, codigo_postal: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              ) : (
                <p className="text-white mt-1">{customer.codigo_postal || "-"}</p>
              )}
            </div>

            {/* Localidad */}
            <div>
              <Label className="text-slate-400 text-sm">Localidad</Label>
              {editing ? (
                <Input
                  value={editData.localidad || ""}
                  onChange={(e) => setEditData({ ...editData, localidad: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              ) : (
                <p className="text-white mt-1">{customer.localidad || "-"}</p>
              )}
            </div>

            {/* Provincia */}
            <div>
              <Label className="text-slate-400 text-sm">Provincia</Label>
              {editing ? (
                <Input
                  value={editData.ciudad || ""}
                  onChange={(e) => setEditData({ ...editData, ciudad: e.target.value })}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              ) : (
                <p className="text-white mt-1">{customer.ciudad || "-"}</p>
              )}
            </div>

            {/* RESIDENCIAL: Teléfono + Email */}
            {customer.customer_type === "RESIDENCIAL" && (
              <>
                <div>
                  <Label className="text-slate-400 text-sm">Teléfono</Label>
                  {editing ? (
                    <>
                      <div className="mt-1 flex gap-2">
                        <Select
                          value={editData.phone_country || "ES"}
                          onValueChange={(v) => setEditData({ ...editData, phone_country: v })}
                        >
                          <SelectTrigger data-testid="detail-phone-country-select" className="w-44 bg-[#0A0A35] border-[#1E1E5F] text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-72">
                            {COUNTRY_DIRECTORY.map(c => (
                              <SelectItem key={c.iso} value={c.iso} className="text-white">{formatCountryLabel(c)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={editData.phone || ""}
                          onChange={(e) => {
                            const detected = detectCountryFromPhone(e.target.value);
                            setEditData({
                              ...editData,
                              phone: e.target.value,
                              phone_country: detected || editData.phone_country || "ES",
                            });
                          }}
                          placeholder="612 345 678 o +34 612 345 678"
                          className={`flex-1 bg-[#0A0A35] text-white ${inputBorderForValidation(phoneValidation)}`}
                        />
                      </div>
                      <ValidationHint result={phoneValidation} />
                    </>
                  ) : (
                    <p className="text-white mt-1">{formatPhoneWithCountry(customer.phone, customer.phone_country) || "-"}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-400 text-sm">Correo Electrónico</Label>
                  {editing ? (
                    <>
                      <Input
                        value={editData.email || ""}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        placeholder="Añada un correo electrónico"
                        className={`mt-1 bg-[#0A0A35] text-white ${inputBorderForValidation(emailValidation)}`}
                      />
                      <ValidationHint result={emailValidation} />
                    </>
                  ) : (
                    <p className="text-white mt-1">{customer.email || "-"}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {/* === SECTION: Datos del representante (EMPRESA only) === */}
        {customer.customer_type === "EMPRESA" && (
          <section>
            <h4 className="text-[#42E695] uppercase text-xs font-semibold tracking-wider mb-4 pb-2 border-b border-[#1E1E5F]">
              Datos del representante
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="md:col-span-2">
                <Label className="text-slate-400 text-sm">Nombre del representante</Label>
                {editing ? (
                  <Input
                    value={editData.representante_nombre || ""}
                    onChange={(e) => setEditData({ ...editData, representante_nombre: e.target.value })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                ) : (
                  <p className="text-white mt-1">{customer.representante_nombre || "-"}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-400 text-sm">
                  {customer.representante_document_type === "NIE"
                    ? "NIE representante"
                    : customer.representante_document_type === "PASAPORTE"
                      ? "Pasaporte representante"
                      : customer.representante_document_type === "DNI"
                        ? "DNI representante"
                        : "Documento representante"}
                </Label>
                {editing ? (
                  <>
                    <div className="mt-1 flex gap-2">
                      <Select
                        value={editData.representante_document_type || "DNI"}
                        onValueChange={(v) => setEditData({ ...editData, representante_document_type: v })}
                      >
                        <SelectTrigger data-testid="detail-rep-document-type-select" className="w-32 bg-[#0A0A35] border-[#1E1E5F] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                          <SelectItem value="DNI" className="text-white">DNI</SelectItem>
                          <SelectItem value="NIE" className="text-white">NIE</SelectItem>
                          <SelectItem value="PASAPORTE" className="text-white">Pasaporte</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={editData.dni_representante || ""}
                        onChange={(e) => setEditData({ ...editData, dni_representante: e.target.value })}
                        className={`flex-1 bg-[#0A0A35] text-white ${inputBorderForValidation(repDocValidation)}`}
                      />
                    </div>
                    <ValidationHint result={repDocValidation} />
                  </>
                ) : (
                  <p className="text-white mt-1">{customer.dni_representante || "-"}</p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* === SECTION: Datos de contacto (EMPRESA only) === */}
        {customer.customer_type === "EMPRESA" && (
          <section>
            <h4 className="text-[#42E695] uppercase text-xs font-semibold tracking-wider mb-4 pb-2 border-b border-[#1E1E5F]">
              Datos de contacto
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <Label className="text-slate-400 text-sm">Persona de Contacto</Label>
                {editing ? (
                  <Input
                    value={editData.persona_contacto || ""}
                    onChange={(e) => setEditData({ ...editData, persona_contacto: e.target.value })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                ) : (
                  <p className="text-white mt-1">{customer.persona_contacto || "-"}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-400 text-sm">Teléfono</Label>
                {editing ? (
                  <>
                    <div className="mt-1 flex gap-2">
                      <Select
                        value={editData.phone_country || "ES"}
                        onValueChange={(v) => setEditData({ ...editData, phone_country: v })}
                      >
                        <SelectTrigger data-testid="detail-phone-country-select" className="w-44 bg-[#0A0A35] border-[#1E1E5F] text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-72">
                          {COUNTRY_DIRECTORY.map(c => (
                            <SelectItem key={c.iso} value={c.iso} className="text-white">{formatCountryLabel(c)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={editData.phone || ""}
                        onChange={(e) => {
                          const detected = detectCountryFromPhone(e.target.value);
                          setEditData({
                            ...editData,
                            phone: e.target.value,
                            phone_country: detected || editData.phone_country || "ES",
                          });
                        }}
                        placeholder="612 345 678 o +34 612 345 678"
                        className={`flex-1 bg-[#0A0A35] text-white ${inputBorderForValidation(phoneValidation)}`}
                      />
                    </div>
                    <ValidationHint result={phoneValidation} />
                  </>
                ) : (
                  <p className="text-white mt-1">{formatPhoneWithCountry(customer.phone, customer.phone_country) || "-"}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-400 text-sm">Correo Electrónico</Label>
                {editing ? (
                  <>
                    <Input
                      value={editData.email || ""}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      placeholder="Añada un correo electrónico"
                      className={`mt-1 bg-[#0A0A35] text-white ${inputBorderForValidation(emailValidation)}`}
                    />
                    <ValidationHint result={emailValidation} />
                  </>
                ) : (
                  <p className="text-white mt-1">{customer.email || "-"}</p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* === SECTION: Facturación === */}
        <section>
          <h4 className="text-[#42E695] uppercase text-xs font-semibold tracking-wider mb-4 pb-2 border-b border-[#1E1E5F]">
            Facturación
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-sm">IBAN</Label>
              {editing ? (
                <>
                  <Input
                    value={editData.iban || ""}
                    onChange={(e) => setEditData({ ...editData, iban: e.target.value })}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    className={`mt-1 bg-[#0A0A35] text-white ${inputBorderForValidation(ibanValidation)}`}
                  />
                  <ValidationHint result={ibanValidation} />
                </>
              ) : (
                <p className="text-white mt-1 font-mono">{customer.iban || "-"}</p>
              )}
            </div>
          </div>
        </section>

        {/* Outside any section: Ejecutivo de Ventas + Notas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 border-t border-[#1E1E5F]/50">
          <div>
            <Label className="text-slate-400 text-sm">Ejecutivo de Ventas</Label>
            {editing ? (
              <Select
                value={editData.sales_executive_id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    setEditData({ ...editData, sales_executive_id: null, sales_executive_name: null });
                  } else {
                    const exec = users.find(u => u.id === v);
                    setEditData({ ...editData, sales_executive_id: v, sales_executive_name: exec?.name || "" });
                  }
                }}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="customer-sales-executive-select">
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="__none__" className="text-slate-400">— Sin asignar —</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-white mt-1">{customer.sales_executive_name || "-"}</p>
            )}
          </div>
          <div className="md:col-span-4">
            <Label className="text-slate-400 text-sm">Notas</Label>
            {editing ? (
              <Textarea
                value={editData.notas || ""}
                onChange={(e) => setEditData({ ...editData, notas: e.target.value })}
                placeholder="Notas adicionales sobre el cliente..."
                rows={3}
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            ) : (
              <p className="text-white mt-1 whitespace-pre-wrap">{customer.notas || "-"}</p>
            )}
          </div>
        </div>

        {/* Footer info */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mt-8 pt-6 border-t border-[#1E1E5F]">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-slate-400">Servicios activos: </span>
              <span className="text-[#42E695] font-bold">{customer.active_services || services.length}</span>
            </div>
            <div>
              <span className="text-slate-400">Alta realizada el: </span>
              <span className="text-white">{formatDateTime(customer.created_at)}</span>
            </div>
          </div>
          <div className="mt-4 md:mt-0">
            {editing ? (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setEditing(false)}
                  className="text-slate-400"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-[#42E695] text-[#04042C]"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Guardar
                </Button>
              </div>
            ) : (
              canEdit("customers") && (
                <Button
                  onClick={() => setEditing(true)}
                  className="bg-[#03BEE4] text-white hover:bg-[#03BEE4]/80"
                  data-testid="customer-edit-btn"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar Datos
                </Button>
              )
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
